import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  ListItemAvatar,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ButtonGroup,
  Tooltip,
  Grid,
  Divider,
  Menu,
  Drawer,
  useMediaQuery
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import BottomSheetHandle from '../components/atoms/BottomSheetHandle';
import {
  Email as EmailIcon,
  Article as ArticleIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  DragIndicator as DragIndicatorIcon,
  ArrowUpward as ArrowUpwardIcon,
  CheckCircle as CheckCircleIcon,
  MarkEmailUnread as UnsentIcon,
  CalendarMonth as CalendarMonthIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  FilterList as FilterListIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import api from '../services/api';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { parseSubscribers } from '../utils/subscriberParser';
import { useBrand } from '../context/BrandContext';
import { useTheme } from '../design-system/providers/ThemeProvider';
import NewsletterOrderDialog from '../components/organisms/Dialogs/NewsletterOrderDialog/NewsletterOrderDialog';
import NewsletterFinalOrderDialog from '../components/organisms/Dialogs/NewsletterOrderDialog/NewsletterFinalOrderDialog';
import DateRangePicker from '../components/molecules/DateRangePicker/DateRangePicker';
import ModernToast from '../components/molecules/ModernToast/ModernToast';
import NewsletterTabs from '../components/molecules/NewsletterTabs/NewsletterTabs';
import PlatformDialog from '../components/molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../components/molecules/PlatformDialog/PlatformDialogTitle';
import { getEffectiveTier } from '../utils/effectiveTier';

const ALL_TAGS_VALUE = '__all_tags__';
const CLEAR_TAGS_VALUE = '__clear_tags__';
const UNTAGGED_VALUE = '__untagged__';

// Períodos rápidos
const PERIOD_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: '24h', label: '24h' },
  { value: '48h', label: '48h' },
  { value: '3d', label: '3 dias' },
  { value: '7d', label: '7 dias' },
  { value: '30d', label: '30 dias' },
  { value: 'custom', label: 'Personalizado' },
];

// Colunas da tabela
const TABLE_COLUMNS = [
  { id: 'title',      label: 'Título',         sortable: true,  width: '34%', minWidth: 200 },
  { id: 'siteName',   label: 'Fonte',          sortable: true,  width: '20%', minWidth: 140 },
  { id: 'date',       label: 'Data Pub.',      sortable: true,  width: '10%', minWidth: 80, maxWidth: 100 },
  { id: 'mediaType',  label: 'Tipo',           sortable: true,  width: '8%',  minWidth: 60, maxWidth: 80 },
  { id: 'tier',       label: 'Tier',           sortable: true,  width: '7%',  minWidth: 50, maxWidth: 70 },
  { id: 'valuation',  label: 'Valoração',      sortable: true,  width: '9%',  minWidth: 70, maxWidth: 90 },
  { id: 'audience',   label: 'Audiência',      sortable: true,  width: '8%',  minWidth: 60, maxWidth: 80 },
  { id: 'actions',    label: '',               sortable: false, width: '4%',  minWidth: 40, maxWidth: 50 },
];

const getSortValue = (article, col) => {
  switch (col) {
    case 'title':      return (article.title || '').toLowerCase();
    case 'siteName':   return (article.siteName || '').toLowerCase();
    case 'date':       return new Date(article.date || article.createdAt).getTime();
    case 'mediaType':  return (article.mediaType || '').toLowerCase();
    case 'tier': {
      return getEffectiveTier(article) || 9;
    }
    case 'valuation':  return article.valuation || 0;
    case 'audience':   return parseSubscribers(article.subscribers || article.views || article.reach || '0');
    default:           return 0;
  }
};

const getNewsletterSourceKey = (article = {}) => {
  if (article.siteUrl) {
    try {
      const normalizedUrl = /^https?:\/\//i.test(article.siteUrl) ? article.siteUrl : `https://${article.siteUrl}`;
      const host = new URL(normalizedUrl).hostname.replace(/^www\./i, '').toLowerCase();
      if (host) return `domain:${host}`;
    } catch (_) {
      // Usa os identificadores alternativos abaixo.
    }
  }
  if (article.username) return `profile:${String(article.username).trim().toLowerCase()}`;
  return `name:${String(article.siteName || 'fonte-desconhecida').trim().toLowerCase()}`;
};

const NewsletterPage = () => {
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const { activeBrandData, updateBrand } = useBrand();
  const { fontFamily: systemFontFamily } = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Contatos
  const [contacts, setContacts] = useState([]);
  const [newContact, setNewContact] = useState({ name: '', email: '', groups: [] });
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [availableCollections, setAvailableCollections] = useState([]);

  // Artigos
  const [articles, setArticles] = useState([]);
  const [selectedArticles, setSelectedArticles] = useState([]);

  // Filtros
  const [searchText, setSearchText] = useState('');
  const [period, setPeriod] = useState('24h');
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [customDateRange, setCustomDateRange] = useState(null);
  const [mediaTypeFilter, setMediaTypeFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  // null = todas as tags (estado dinâmico); [] = nenhuma tag.
  const [tagsFilter, setTagsFilter] = useState(null);
  const [onlyUnsent, setOnlyUnsent] = useState(true); // ← Iniciando como true para performance

  // Ordenação da tabela
  const [sortCol, setSortCol] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  // Grupos
  const [groups, setGroups] = useState([]);
  const [quickGroupDialogOpen, setQuickGroupDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);

  // Newsletter
  const [newsletter, setNewsletter] = useState({
    subject: '', message: '', templateId: '',
    selectedContacts: [], selectedGroups: [], groupByTags: false, showImages: true
  });
  const [templates, setTemplates] = useState([]);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderHistory, setOrderHistory] = useState([]);
  const [orderHistoryLoading, setOrderHistoryLoading] = useState(false);
  const [finalOrderDialogOpen, setFinalOrderDialogOpen] = useState(false);
  const [finalOrderTagsOrder, setFinalOrderTagsOrder] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Selecionar Artigos', 'Escolher Destinatários', 'Criar Newsletter', 'Enviar'];
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);

  // Estados de CRUD (Edição/Exclusão)
  const [editingContact, setEditingContact] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'contact' | 'group', data: any }
  const [deleteCascade, setDeleteCascade] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuData, setMenuData] = useState(null); // { type: 'contact' | 'group', item: any }
  const [editGroupContactIds, setEditGroupContactIds] = useState([]);
  const [groupContactSearch, setGroupContactSearch] = useState('');

  useEffect(() => {
    if (activeBrandData?.name && !newsletter.subject) {
      const formatter = new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });
      setNewsletter(prev => ({
        ...prev,
        subject: `Newsletter ${activeBrandData.name} - ${formatter.format(new Date())}`
      }));
    }
  }, [activeBrandData]);

  useEffect(() => {
    loadContacts();
    loadGroups();
    loadTemplates();
    loadFilterMetadata();
    if (location.state?.selectedArticleIds) {
      handleInitialSelection(location.state.selectedArticleIds);
    }
    if (location.state?.resendNewsletter) {
      const rn = location.state.resendNewsletter;
      setNewsletter(prev => ({
        ...prev,
        subject: rn.subject || prev.subject,
        message: rn.message || prev.message,
        templateId: rn.template || prev.templateId,
        selectedContacts: rn.recipients || [],
        selectedGroups: rn.groups || [],
        groupByTags: Boolean(rn.groupByTags),
        showImages: rn.showImages !== false
      }));
    }
    // Padrão: carregar apenas as últimas 24h e não enviados para performance
    loadArticles({ period: '24h', onlyUnsent: true });
  }, []);

  useEffect(() => { setActiveTab(activeStep); }, [activeStep]);

  const loadFilterMetadata = async () => {
    try {
      const [tagsRes, collectionsRes] = await Promise.all([api.get('/tags'), api.get('/collections')]);
      setAvailableTags(tagsRes.data.data || []);
      setAvailableCollections(collectionsRes.data.data || []);
    } catch (err) { console.error(err); }
  };

  const loadGroups = async () => {
    try {
      const response = await api.get('/newsletter/groups');
      setGroups(response.data.data || []);
    } catch (err) {
      console.error('Erro ao carregar grupos:', err);
    }
  };

  const handleCreateGroupFromSelection = async () => {
    if (!newGroupName.trim()) {
      setError('Por favor, dê um nome ao grupo');
      setSnackbarOpen(true);
      return;
    }

    try {
      setCreatingGroup(true);
      const response = await api.post('/newsletter/groups', {
        name: newGroupName,
        contacts: newsletter.selectedContacts
      });

      if (response.data.success) {
        setSuccess('Grupo criado com sucesso!');
        loadGroups();
        // Adiciona o grupo recém criado à seleção e limpa os contatos individuais (para ser mais organizado)
        setNewsletter(prev => ({
          ...prev,
          selectedGroups: [...prev.selectedGroups, response.data.data._id],
          selectedContacts: []
        }));
        setQuickGroupDialogOpen(false);
        setNewGroupName('');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao criar grupo');
      setSnackbarOpen(true);
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleInitialSelection = async (ids) => {
    try {
      setLoading(true);
      const response = await api.post('/articles/by-ids', { ids });
      const pre = response.data.data || response.data.articles || response.data;
      if (Array.isArray(pre)) setSelectedArticles(pre);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const loadContacts = async () => {
    try {
      const r = await api.get('/newsletter/contacts?limit=9999');
      setContacts(r.data.data);
    } catch (err) { console.error(err); }
  };

  // Handlers de Menu e Ações
  const handleMenuOpen = (event, type, item) => {
    setAnchorEl(event.currentTarget);
    setMenuData({ type, item });
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuData(null);
  };

  const handleDeleteRequest = (type, item) => {
    handleMenuClose();
    setDeleteTarget({ type, data: item });
    setDeleteCascade(false);
    setDeleteConfirmOpen(true);
  };

  const handleEditRequest = (type, item) => {
    handleMenuClose();
    if (type === 'contact') {
      setEditingContact(item);
      setNewContact({ name: item.name, email: item.email, groups: item.groups || [] });
      setContactDialogOpen(true);
    } else {
      setEditingGroup(item);
      setNewGroupName(item.name);
      // Extrair IDs dos contatos atuais do grupo
      setEditGroupContactIds(item.contacts?.map(c => typeof c === 'string' ? c : c._id) || []);
      setGroupContactSearch('');
      setQuickGroupDialogOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      setLoading(true);
      const { type, data } = deleteTarget;
      const url = type === 'contact' ? `/newsletter/contacts/${data._id}` : `/newsletter/groups/${data._id}`;
      const params = type === 'group' && deleteCascade ? '?deleteContacts=true' : '';
      
      await api.delete(`${url}${params}`);
      
      setSuccess(`${type === 'contact' ? 'Contato' : 'Grupo'} excluído com sucesso`);
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
      
      // Recarregar dados
      loadContacts();
      loadGroups();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao excluir');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContact = async () => {
    if (!newContact.name || !newContact.email) {
      setError('Preencha nome e email');
      setSnackbarOpen(true);
      return;
    }

    try {
      setLoading(true);
      if (editingContact) {
        await api.put(`/newsletter/contacts/${editingContact._id}`, newContact);
        setSuccess('Contato atualizado com sucesso');
      } else {
        await api.post('/newsletter/contacts', newContact);
        setSuccess('Contato criado com sucesso');
      }
      
      setContactDialogOpen(false);
      setNewContact({ name: '', email: '', groups: [] });
      setEditingContact(null);
      loadContacts();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao processar contato');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateGroupBasic = async () => {
    if (!newGroupName.trim() || !editingGroup) return;

    try {
      setCreatingGroup(true);
      await api.put(`/newsletter/groups/${editingGroup._id}`, { 
        name: newGroupName,
        contacts: editGroupContactIds 
      });
      setSuccess('Grupo atualizado com sucesso');
      loadGroups();
      setQuickGroupDialogOpen(false);
      setNewGroupName('');
      setEditingGroup(null);
      setEditGroupContactIds([]);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao atualizar grupo');
      setSnackbarOpen(true);
    } finally {
      setCreatingGroup(false);
    }
  };

  const tagSelectValue = tagsFilter === null
    ? [...availableTags.map(tag => String(tag._id)), UNTAGGED_VALUE]
    : tagsFilter;

  const handleTagsFilterChange = (event) => {
    const next = event.target.value;
    if (next.includes(ALL_TAGS_VALUE)) {
      setTagsFilter(null);
      return;
    }
    if (next.includes(CLEAR_TAGS_VALUE)) {
      setTagsFilter([]);
      return;
    }
    setTagsFilter(next);
  };

  const selectedTagChips = tagsFilter === null
    ? [{ id: null, label: 'Todas as tags' }]
    : tagsFilter.length === 0
      ? [{ id: null, label: 'Nenhuma tag selecionada' }]
      : tagsFilter.map(tagId => ({
          id: tagId,
          label: tagId === UNTAGGED_VALUE
            ? 'Sem tag'
            : availableTags.find(tag => String(tag._id) === tagId)?.name || 'Tag indisponível'
        }));

  const renderSelectedTagChips = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
      <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>
        Tags selecionadas:
      </Typography>
      {selectedTagChips.map(tag => (
        <Chip
          key={tag.id || tag.label}
          label={tag.label}
          size="small"
          color={tagsFilter === null ? 'primary' : 'default'}
          variant="outlined"
          onDelete={tag.id ? () => setTagsFilter(current => current.filter(id => id !== tag.id)) : undefined}
          sx={{ height: 24, maxWidth: 240, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
        />
      ))}
    </Box>
  );

  const buildParams = (overrides = {}) => {
    const params = new URLSearchParams();
    const s = overrides.search !== undefined ? overrides.search : searchText;
    const mt = overrides.mediaType !== undefined ? overrides.mediaType : mediaTypeFilter;
    const t = overrides.tier !== undefined ? overrides.tier : tierFilter;
    const tg = overrides.tags !== undefined ? overrides.tags : tagsFilter;
    const p = overrides.period !== undefined ? overrides.period : period;
    const dr = overrides.dateRange !== undefined ? overrides.dateRange : customDateRange;
    const unsent = overrides.onlyUnsent !== undefined ? overrides.onlyUnsent : onlyUnsent;

    if (s) params.append('search', s);
    if (mt) params.append('mediaType', mt);
    if (t) params.append('tier', t);
    if (tg !== null) {
      const selectedTags = Array.isArray(tg) ? tg : [tg];
      const tagIds = selectedTags.filter(tagId => tagId !== UNTAGGED_VALUE);
      if (tagIds.length > 0) params.append('tags', tagIds.join(','));
      if (selectedTags.includes(UNTAGGED_VALUE)) params.append('includeUntagged', 'true');
      if (selectedTags.length === 0) params.append('noTagsSelected', 'true');
    }
    if (unsent) params.append('notSentInNewsletter', 'true');

    if (p && p !== 'all' && p !== 'custom') {
      const now = new Date();
      const ms = { 
        '24h': 86400000, 
        '48h': 172800000, 
        '3d': 259200000, 
        '7d': 604800000, 
        '30d': 2592000000 
      };
      if (ms[p]) {
        params.append('startDate', new Date(now - ms[p]).toISOString());
        params.append('dateType', 'created'); // Forçar busca por data de cadastro nos filtros rápidos
      }
    }
    if (p === 'custom' && dr) {
      const startOfDay = new Date(dr.startDate);
      const endOfDay = new Date(dr.endDate);
      startOfDay.setHours(0, 0, 0, 0);
      endOfDay.setHours(23, 59, 59, 999);
      params.append('startDate', startOfDay.toISOString());
      params.append('endDate', endOfDay.toISOString());
      params.append('dateType', dr.dateType || 'published');
    }
    return params;
  };

  const loadArticles = async (overrides = {}) => {
    try {
      setLoading(true);
      const params = buildParams(overrides);
      const r = await api.get(`/newsletter/articles?${params}`);
      setArticles(r.data.data || []);
    } catch (err) {
      setError('Erro ao carregar artigos');
    } finally {
      setLoading(false);
    }
  };

  // Ordenação da tabela (clique na coluna)
  const handleSortColumn = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir(col === 'title' || col === 'siteName' || col === 'mediaType' ? 'asc' : 'desc');
    }
  };

  const sortedArticles = [...articles].sort((a, b) => {
    const aVal = getSortValue(a, sortCol);
    const bVal = getSortValue(b, sortCol);
    if (aVal === bVal) return 0;
    const cmp = typeof aVal === 'string' ? aVal.localeCompare(bVal, 'pt') : aVal - bVal;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const selectedIds = React.useMemo(() => new Set(selectedArticles.map(a => a._id)), [selectedArticles]);
  const isAllSelected = sortedArticles.length > 0 && sortedArticles.every(a => selectedIds.has(a._id));
  const isAnySelected = sortedArticles.length > 0 && sortedArticles.some(a => selectedIds.has(a._id));
  const isIndeterminate = isAnySelected && !isAllSelected;

  const handleArticleSelect = (article) => {
    setSelectedArticles(prev => {
      const exists = prev.find(a => a._id === article._id);
      if (exists) return prev.filter(a => a._id !== article._id);
      return [...prev, article];
    });
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(selectedArticles);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setSelectedArticles(items);
  };

  const handleApplyOrder = async (reordered, config) => {
    setSelectedArticles(reordered);
    setNewsletter(prev => ({ ...prev, groupByTags: true }));
    setOrderDialogOpen(false);
    setFinalOrderTagsOrder(config.orderedTags);
    if (activeBrandData?._id && config.shouldPersist !== false) {
      await updateBrand(activeBrandData._id, {
        newsletterOrderTemplate: { tagsOrder: config.orderedTags, sortCriteria: config.sortCriteria }
      });
    }
    setFinalOrderDialogOpen(true);
  };

  const handleConfirmFinalOrder = async (final) => {
    setSelectedArticles(final);
    setFinalOrderDialogOpen(false);

    if (!activeBrandData?._id) return;

    const currentSources = [];
    const currentSourceKeys = new Set();
    final.forEach(article => {
      const sourceKey = getNewsletterSourceKey(article);
      if (!currentSourceKeys.has(sourceKey)) {
        currentSourceKeys.add(sourceKey);
        currentSources.push({ sourceKey, sourceName: article.siteName || article.username || 'Fonte sem nome' });
      }
    });

    const previousOrder = [...(activeBrandData.newsletterSourceOrder || [])]
      .sort((a, b) => a.position - b.position);
    const previousByKey = new Map(previousOrder.map(source => [source.sourceKey, source]));
    const updatedOrder = previousOrder.map(source => ({ ...source }));
    const occupiedSlots = updatedOrder
      .map((source, index) => currentSourceKeys.has(source.sourceKey) ? index : -1)
      .filter(index => index >= 0);
    const existingCurrentSources = currentSources.filter(source => previousByKey.has(source.sourceKey));
    const now = new Date().toISOString();

    occupiedSlots.forEach((slot, index) => {
      const source = existingCurrentSources[index];
      const previous = previousByKey.get(source.sourceKey);
      updatedOrder[slot] = {
        ...previous,
        ...source,
        selectionCount: (previous.selectionCount || 0) + 1,
        lastExplicitlyOrderedAt: now
      };
    });

    currentSources.forEach((source, currentIndex) => {
      if (previousByKey.has(source.sourceKey)) return;
      const newEntry = {
        ...source,
        isPinned: false,
        selectionCount: 1,
        lastExplicitlyOrderedAt: now
      };
      const nextKnown = currentSources
        .slice(currentIndex + 1)
        .find(candidate => updatedOrder.some(item => item.sourceKey === candidate.sourceKey));
      if (nextKnown) {
        const nextIndex = updatedOrder.findIndex(item => item.sourceKey === nextKnown.sourceKey);
        updatedOrder.splice(nextIndex, 0, newEntry);
        return;
      }
      const previousKnown = [...currentSources.slice(0, currentIndex)]
        .reverse()
        .find(candidate => updatedOrder.some(item => item.sourceKey === candidate.sourceKey));
      if (previousKnown) {
        const previousIndex = updatedOrder.findIndex(item => item.sourceKey === previousKnown.sourceKey);
        updatedOrder.splice(previousIndex + 1, 0, newEntry);
        return;
      }
      updatedOrder.push(newEntry);
    });

    try {
      await updateBrand(activeBrandData._id, {
        newsletterSourceOrder: updatedOrder.map((source, position) => ({ ...source, position }))
      });
      setSuccess('Ordem ajustada e estrutura de fontes atualizada para os próximos envios.');
    } catch (sourceOrderError) {
      console.error('Erro ao salvar estrutura de fontes:', sourceOrderError);
      setError('A ordem foi aplicada neste envio, mas não foi possível salvar a estrutura de fontes.');
    }
  };

  const openOrderDialog = async () => {
    setOrderDialogOpen(true);
    setOrderHistoryLoading(true);
    try {
      const response = await api.get('/newsletter?limit=10&status=sent');
      setOrderHistory(response.data?.data || []);
    } catch (historyError) {
      console.error('Erro ao carregar histórico de ordenação:', historyError);
      setOrderHistory([]);
    } finally {
      setOrderHistoryLoading(false);
    }
  };

  const handlePreviewNewsletter = async () => {
    if (!newsletter.templateId) { setError('Selecione um template'); setSnackbarOpen(true); return; }
    const templateTagsOrder = activeBrandData?.newsletterOrderTemplate?.tagsOrder || [];
    try {
      setPreviewLoading(true);
      const r = await api.post('/newsletter/preview', {
        subject: newsletter.subject, message: newsletter.message,
        templateId: newsletter.templateId, articleIds: selectedArticles.map(a => a._id),
        groupByTags: newsletter.groupByTags, showImages: newsletter.showImages,
        tagsOrder: templateTagsOrder
      });
      if (r.data.success) {
        setPreviewHtml(r.data.html);
        if (isMobile) setMobilePreviewOpen(true);
      }
    } catch { setError('Erro ao gerar preview'); setSnackbarOpen(true); }
    finally { setPreviewLoading(false); }
  };

  const loadTemplates = async () => {
    try {
      // Buscar preferência do usuário e templates
      const [userRes, r] = await Promise.all([
        api.get('/auth/me'),
        api.get('/newsletter/templates')
      ]);
      
      setTemplates(r.data.data);
      
      if (r.data.data?.length > 0) {
        const preferredTemplateId = userRes.data.data?.user?.preferredTemplateId;
        let def;
        
        // Primeiro: template preferido do usuário
        if (preferredTemplateId) {
          def = r.data.data.find(t => t.isActive && t._id === preferredTemplateId);
        }
        
        // Segundo: template padrão
        if (!def) {
          def = r.data.data.find(t => t.isActive && t.isDefault);
        }
        
        // Terceiro: primeiro disponível
        if (!def) {
          def = r.data.data.find(t => t.isActive);
        }
        
        if (def) {
          setNewsletter(prev => {
            if (prev.templateId) return prev;
            return { ...prev, templateId: def._id.toString() };
          });
        }
      }
    } catch (err) { console.error(err); }
  };

  // Salvar preferência de template
  const saveTemplatePreference = async (templateId) => {
    try {
      await api.put('/auth/preferred-template', { templateId });
    } catch (err) {
      console.warn('Erro ao salvar preferência de template:', err);
    }
  };



  const handleCreateNewsletter = async () => {
    try {
      setLoading(true);
      if (!newsletter.subject.trim()) { setError('Assunto é obrigatório'); setLoading(false); return; }
      if (selectedArticles.length === 0) { setError('Selecione pelo menos um artigo'); setLoading(false); return; }
      if (newsletter.selectedContacts.length === 0 && (newsletter.selectedGroups?.length || 0) === 0) { 
        setError('Selecione pelo menos um destinatário (contato ou grupo)'); 
        setLoading(false); 
        return; 
      }

      const data = {
        subject: newsletter.subject.trim(), message: newsletter.message?.trim() || '',
        articleIds: selectedArticles.map(a => a._id), recipientIds: newsletter.selectedContacts,
        groups: newsletter.selectedGroups || [], templateId: newsletter.templateId,
        groupByTags: newsletter.groupByTags, showImages: newsletter.showImages,
        tagsOrder: activeBrandData?.newsletterOrderTemplate?.tagsOrder || [],
        fontFamily: systemFontFamily
      };

      const r = await api.post('/newsletter/create', data);
      if (r.data.success && r.data.data._id) {
        try { await api.post(`/newsletter/send/${r.data.data._id}`); setSuccess('Newsletter criada e enviada!'); }
        catch { setSuccess('Newsletter criada! (Verifique logs de envio)'); }
      } else { setSuccess('Newsletter criada!'); }
      setActiveStep(3);
    } catch (err) { setError(err.response?.data?.message || 'Erro ao criar newsletter'); }
    finally { setLoading(false); }
  };

  const handleSendNewsletter = async () => { setSuccess('Newsletter enviada!'); };

  const periodLabel = () => {
    if (period === 'custom' && customDateRange) {
      const fmt = d => d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
      const type = customDateRange.dateType === 'created' ? 'Cadastro' : 'Pub.';
      return `${type}: ${fmt(customDateRange.startDate)}–${fmt(customDateRange.endDate)}`;
    }
    return PERIOD_OPTIONS.find(p => p.value === period)?.label || 'Todos';
  };

  const formatAudience = (article) => {
    const raw = article.subscribers || article.views || article.reach;
    if (!raw) return '—';
    const n = parseSubscribers(String(raw));
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
    return String(n);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // PASSO 1 — Tabela de artigos + painel selecionados
  // ─────────────────────────────────────────────────────────────────────────────
  const renderArticleSelection = () => (
    <Box>

      {/* Controles compactos exclusivos do mobile */}
      <Paper variant="outlined" sx={{ display: { xs: 'flex', md: 'none' }, p: 1.25, mb: 1.5, gap: 1, alignItems: 'center', flexWrap: 'wrap', borderRadius: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={() => setMobileFiltersOpen(true)}
          sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
        >
          Filtros
          {(searchText || mediaTypeFilter || tierFilter || tagsFilter !== null || period !== 'all' || onlyUnsent) && (
            <Chip label="Ativos" size="small" color="primary" sx={{ ml: 'auto', height: 20 }} />
          )}
        </Button>
        <Button
          fullWidth
          variant={onlyUnsent ? 'contained' : 'outlined'}
          color={onlyUnsent ? 'warning' : 'inherit'}
          startIcon={<UnsentIcon />}
          onClick={() => {
            const next = !onlyUnsent;
            setOnlyUnsent(next);
            loadArticles({ onlyUnsent: next });
          }}
          aria-pressed={onlyUnsent}
          sx={{
            minHeight: 40,
            justifyContent: 'flex-start',
            textTransform: 'none',
            fontWeight: 700,
          }}
        >
          Somente artigos não enviados
          <Checkbox
            checked={onlyUnsent}
            size="small"
            tabIndex={-1}
            disableRipple
            sx={{ p: 0, ml: 'auto', color: 'inherit', pointerEvents: 'none' }}
          />
        </Button>
      </Paper>

      {selectedArticles.length > 0 && (
        <Paper
          variant="outlined"
          sx={{ display: { xs: 'block', md: 'none' }, p: 1.25, mb: 1.5, borderRadius: 2, borderColor: 'primary.main', bgcolor: 'action.selected' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CheckCircleIcon color="primary" sx={{ fontSize: 18 }} />
              <Typography variant="body2" fontWeight={700}>
                {selectedArticles.length} artigo{selectedArticles.length === 1 ? '' : 's'} selecionado{selectedArticles.length === 1 ? '' : 's'}
              </Typography>
            </Box>
            <Chip label="Ordenar" size="small" color="primary" sx={{ height: 20, fontSize: '0.65rem' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              variant="contained"
              size="small"
              startIcon={<ArrowUpwardIcon />}
              onClick={openOrderDialog}
              sx={{ textTransform: 'none', fontSize: '0.72rem', fontWeight: 700 }}
            >
              Ordenação automática
            </Button>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<DragIndicatorIcon />}
              onClick={() => {
                setFinalOrderTagsOrder(activeBrandData?.newsletterOrderTemplate?.tagsOrder || []);
                setFinalOrderDialogOpen(true);
              }}
              sx={{ textTransform: 'none', fontSize: '0.72rem', fontWeight: 700 }}
            >
              Ajuste manual
            </Button>
          </Box>
        </Paper>
      )}

      {/* FILTROS */}
      <Paper elevation={0} sx={{ display: { xs: 'none', md: 'block' }, p: 2, mb: 2, bgcolor: 'background.paper', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        
        {/* Linha 1 */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Buscar artigos" size="small" value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadArticles()}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Tipo</InputLabel>
            <Select value={mediaTypeFilter} onChange={(e) => setMediaTypeFilter(e.target.value)} label="Tipo">
              <MenuItem value="">Todos</MenuItem>
              {['Online','YouTube','Instagram','Twitter','TikTok','Facebook','LinkedIn','Jornal','Revista','TV','Rádio'].map(t => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 85 }}>
            <InputLabel>Tier</InputLabel>
            <Select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} label="Tier">
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="Tier 1">T1</MenuItem>
              <MenuItem value="Tier 2">T2</MenuItem>
              <MenuItem value="Tier 3">T3</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Tags</InputLabel>
            <Select
              multiple
              value={tagSelectValue}
              onChange={handleTagsFilterChange}
              label="Tags"
              renderValue={() => tagsFilter === null
                ? 'Todas as tags'
                : tagsFilter.length === 0
                  ? 'Nenhuma tag'
                  : `${tagsFilter.length} selecionada(s)`}
            >
              <MenuItem value={ALL_TAGS_VALUE}>
                <Checkbox checked={tagsFilter === null} />
                <ListItemText primary="Selecionar todas" />
              </MenuItem>
              <MenuItem value={CLEAR_TAGS_VALUE}>
                <Checkbox checked={Array.isArray(tagsFilter) && tagsFilter.length === 0} />
                <ListItemText primary="Limpar seleção" />
              </MenuItem>
              <Divider />
              {availableTags.map(tag => (
                <MenuItem key={tag._id} value={String(tag._id)}>
                  <Checkbox checked={tagsFilter === null || tagsFilter.includes(String(tag._id))} />
                  <ListItemText primary={tag.name} />
                </MenuItem>
              ))}
              <MenuItem value={UNTAGGED_VALUE}>
                <Checkbox checked={tagsFilter === null || tagsFilter.includes(UNTAGGED_VALUE)} />
                <ListItemText primary="Sem tag" />
              </MenuItem>
            </Select>
          </FormControl>

          {/* Toggle: Apenas Não Enviados */}
          <Tooltip title={onlyUnsent ? 'Mostrando apenas não enviados — clique para ver todos' : 'Clique para ver apenas artigos não enviados'}>
            <Button
              variant={onlyUnsent ? 'contained' : 'outlined'}
              size="medium"
              color={onlyUnsent ? 'warning' : 'inherit'}
              startIcon={<UnsentIcon />}
              onClick={() => {
                const next = !onlyUnsent;
                setOnlyUnsent(next);
                loadArticles({ onlyUnsent: next });
              }}
              sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              Não enviados
            </Button>
          </Tooltip>

          <Button variant="contained" size="medium" onClick={() => loadArticles()} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            Buscar
          </Button>
          {(searchText || mediaTypeFilter || tierFilter || tagsFilter !== null || period !== 'all' || onlyUnsent) && (
            <Button variant="text" size="small" onClick={() => {
              setSearchText(''); setMediaTypeFilter(''); setTierFilter(''); setTagsFilter(null);
              setPeriod('all'); setCustomDateRange(null); setOnlyUnsent(false);
              loadArticles({ search: '', mediaType: '', tier: '', tags: null, period: 'all', onlyUnsent: false, dateRange: null });
            }} sx={{ color: 'text.secondary', borderRadius: 2 }}>
              Limpar
            </Button>
          )}
        </Box>

        <Box sx={{ mb: 1.5 }}>
          {renderSelectedTagChips()}
        </Box>

        {/* Linha 2: Período */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Período:</Typography>
          <ButtonGroup size="small" variant="outlined" sx={{ borderRadius: 2, '& .MuiButtonGroup-grouped': { borderColor: 'divider' } }}>
            {PERIOD_OPTIONS.map(opt => (
              <Button key={opt.value}
                variant={period === opt.value ? 'contained' : 'outlined'}
                onClick={() => {
                  if (opt.value === 'custom') { setDateRangeOpen(true); return; }
                  setPeriod(opt.value); setCustomDateRange(null);
                  loadArticles({ period: opt.value, dateRange: null });
                }}
                sx={{ 
                  minWidth: 0, 
                  px: 1.2, 
                  fontSize: '0.72rem', 
                  fontWeight: period === opt.value ? 700 : 500,
                  textTransform: 'none'
                }}
              >
                {opt.value === 'custom' && !customDateRange && <CalendarMonthIcon sx={{ fontSize: '0.9rem', mr: 0.5, mb: '-2px' }} />}
                {opt.value === 'custom' && customDateRange ? periodLabel() : opt.label}
              </Button>
            ))}
          </ButtonGroup>

          <Box sx={{ ml: 'auto' }}>
            <Typography variant="caption" color="text.secondary">
              {loading ? 'Buscando...' : `${articles.length} artigo(s) · ${selectedArticles.length} selecionado(s)`}
              {onlyUnsent && <Chip label="Apenas não enviados" size="small" color="warning" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {selectedArticles.length > 0 && (
        <Paper
          variant="outlined"
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            borderColor: 'primary.main',
            bgcolor: 'action.selected'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="primary" />
            <Box>
              <Typography variant="body2" fontWeight={800}>
                {selectedArticles.length} artigo{selectedArticles.length === 1 ? '' : 's'} selecionado{selectedArticles.length === 1 ? '' : 's'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Escolha como organizar ou faça um ajuste manual da ordem.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<ArrowUpwardIcon />}
              onClick={openOrderDialog}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Ordenação automática
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DragIndicatorIcon />}
              onClick={() => {
                setFinalOrderTagsOrder(activeBrandData?.newsletterOrderTemplate?.tagsOrder || []);
                setFinalOrderDialogOpen(true);
              }}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Ajuste manual
            </Button>
          </Box>
        </Paper>
      )}

      {/* TABELA DE ARTIGOS */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : articles.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <ArticleIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
          <Typography variant="body2">Nenhum artigo encontrado. Ajuste os filtros e clique em Buscar.</Typography>
        </Box>
      ) : (
        <>
        <Button
          fullWidth
          variant="text"
          onClick={() => {
            if (isAllSelected) {
              const visibleIds = new Set(sortedArticles.map(a => a._id));
              setSelectedArticles(prev => prev.filter(a => !visibleIds.has(a._id)));
            } else {
              const newOnes = sortedArticles.filter(a => !selectedIds.has(a._id));
              setSelectedArticles(prev => [...prev, ...newOnes]);
            }
          }}
          sx={{
            display: { xs: 'flex', md: 'none' },
            minHeight: 38,
            mb: 0.75,
            px: 0.5,
            justifyContent: 'flex-start',
            color: 'text.primary',
            textTransform: 'none',
            fontSize: '0.78rem',
            fontWeight: 700,
          }}
        >
          <Checkbox
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            size="small"
            tabIndex={-1}
            disableRipple
            sx={{ p: 0.5, mr: 0.5, pointerEvents: 'none' }}
          />
          {isAllSelected ? 'Desmarcar todos os resultados' : 'Selecionar todos os resultados'}
          <Typography component="span" sx={{ ml: 'auto', pr: 0.5, color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600 }}>
            {sortedArticles.length}
          </Typography>
        </Button>
        <Box sx={{ display: { xs: 'grid', md: 'none' }, gap: 1, mb: 2, width: '100%', minWidth: 0, overflow: 'hidden' }}>
          {sortedArticles.map((article) => {
            const isSelected = selectedIds.has(article._id);
            const selectedIndex = selectedArticles.findIndex(a => a._id === article._id);
            return (
              <Paper
                key={article._id}
                variant="outlined"
                onClick={() => handleArticleSelect(article)}
                sx={{
                  p: 1.5,
                  width: '100%',
                  minWidth: 0,
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                  borderRadius: 2,
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  bgcolor: isSelected ? 'action.selected' : 'background.paper',
                  cursor: 'pointer'
                }}
              >
                <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                  <Box sx={{ pt: 0.1 }}>
                    {isSelected ? (
                      <Avatar sx={{ width: 26, height: 26, bgcolor: 'primary.main', fontSize: '0.72rem', fontWeight: 700 }}>
                        {selectedIndex + 1}
                      </Avatar>
                    ) : <Checkbox size="small" checked={false} sx={{ p: 0.25 }} />}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" fontWeight={isSelected ? 700 : 600} sx={{ lineHeight: 1.35, mb: 0.75, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                      {article.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mb: 0.75 }}>
                      {article.siteName || 'Fonte não informada'} · {new Date(article.date || article.createdAt).toLocaleDateString('pt-BR')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {article.mediaType && <Chip label={article.mediaType} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />}
                      {getEffectiveTier(article) && <Chip label={`Tier ${getEffectiveTier(article)}`} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />}
                      <Chip label={`Audiência ${formatAudience(article)}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                      {article.valuation > 0 && <Chip label={`R$ ${Number(article.valuation).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`} size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />}
                    </Box>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
        <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' }, borderRadius: 2, mb: 2, maxHeight: 520, overflowX: 'auto' }}>
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: 1120,
              tableLayout: 'fixed',
              '& .MuiTableCell-root': {
                boxSizing: 'border-box',
                overflow: 'hidden'
              }
            }}
          >
            <TableHead>
              <TableRow>
                {/* Checkbox Selecionar Todos */}
                <TableCell padding="checkbox" sx={{ width: 40, minWidth: 40, bgcolor: 'background.paper' }}>
                  <Tooltip title={selectedArticles.length === sortedArticles.length && sortedArticles.length > 0 ? 'Desmarcar todos' : 'Selecionar todos'}>
                    <Checkbox
                      size="small"
                      indeterminate={isIndeterminate}
                      checked={isAllSelected}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Otimizado: usar Set para busca rápida O(1)
                          const newOnes = sortedArticles.filter(a => !selectedIds.has(a._id));
                          if (newOnes.length > 0) {
                            setSelectedArticles(prev => [...prev, ...newOnes]);
                          }
                        } else {
                          // Otimizado: usar Set para busca rápida O(1)
                          const visibleIds = new Set(sortedArticles.map(a => a._id));
                          setSelectedArticles(prev => prev.filter(s => !visibleIds.has(s._id)));
                        }
                      }}
                    />
                  </Tooltip>
                </TableCell>
                {TABLE_COLUMNS.map(col => (
                  <TableCell key={col.id} sx={{ 
                    width: col.width, 
                    minWidth: col.minWidth || col.width,
                    maxWidth: col.maxWidth,
                    fontWeight: 700, 
                    fontSize: '0.76rem', 
                    bgcolor: 'background.paper',
                    whiteSpace: 'nowrap'
                  }}>
                    {col.sortable ? (
                      <TableSortLabel
                        active={sortCol === col.id}
                        direction={sortCol === col.id ? sortDir : 'asc'}
                        onClick={() => handleSortColumn(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedArticles.map((article) => {
                const isSelected = selectedIds.has(article._id);
                const selIdx = selectedArticles.findIndex(a => a._id === article._id);
                return (
                  <TableRow key={article._id}
                    hover
                    onClick={() => handleArticleSelect(article)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: isSelected ? (theme => theme.palette.mode === 'dark' ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.06)') : 'inherit',
                      borderLeft: '3px solid',
                      borderColor: isSelected ? 'primary.main' : 'transparent',
                      '& td': { borderColor: 'divider' }
                    }}
                  >
                    {/* Checkbox / número de ordem */}
                    <TableCell padding="checkbox" sx={{ pl: 1 }}>
                      {isSelected ? (
                        <Box sx={{
                          width: 22, height: 22, borderRadius: '50%',
                          bgcolor: 'primary.main', color: 'primary.contrastText',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.65rem', fontWeight: 700
                        }}>
                          {selIdx + 1}
                        </Box>
                      ) : (
                        <Checkbox size="small" checked={false}
                          onClick={(e) => { e.stopPropagation(); handleArticleSelect(article); }} />
                      )}
                    </TableCell>

                    {/* Título */}
                    <TableCell>
                      <Typography variant="body2" fontWeight={isSelected ? 600 : 400}
                        sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {article.title}
                      </Typography>
                    </TableCell>

                    {/* Fonte */}
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {article.siteName}
                      </Typography>
                    </TableCell>

                    {/* Data */}
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {new Date(article.date || article.createdAt).toLocaleDateString('pt-BR')}
                      </Typography>
                    </TableCell>

                    {/* Tipo */}
                    <TableCell>
                      {article.mediaType && (
                        <Chip label={article.mediaType} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                      )}
                    </TableCell>

                    {/* Tier */}
                    <TableCell>
                      {getEffectiveTier(article) && (
                        <Chip label={`Tier ${getEffectiveTier(article)}`} size="small"
                          color={getEffectiveTier(article) === 1 ? 'success' : getEffectiveTier(article) === 2 ? 'warning' : 'default'}
                          sx={{ height: 18, fontSize: '0.6rem' }} />
                      )}
                    </TableCell>

                    {/* Valoração */}
                    <TableCell>
                      <Typography variant="caption" color={article.valuation > 0 ? 'success.main' : 'text.disabled'}>
                        {article.valuation > 0 ? `R$ ${Number(article.valuation).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}` : '—'}
                      </Typography>
                    </TableCell>

                    {/* Audiência */}
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatAudience(article)}
                      </Typography>
                    </TableCell>

                    {/* Ação */}
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <IconButton size="small" color={isSelected ? 'error' : 'primary'}
                        onClick={() => handleArticleSelect(article)}>
                        {isSelected ? <DeleteIcon sx={{ fontSize: 16 }} /> : <AddIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        </>
      )}

      {/* PAINEL DE ARTIGOS SELECIONADOS (DRAG & DROP) */}
      {selectedArticles.length > 0 && (
        <Paper elevation={0} sx={{ display: { xs: 'none', md: 'block' }, p: 2, bgcolor: 'background.paper', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color="primary" fontSize="small" />
              <Typography variant="subtitle2" fontWeight={600}>Ordem de Envio</Typography>
              <Chip label={selectedArticles.length} size="small" color="primary" sx={{ height: 18, fontSize: '0.7rem' }} />
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <DragIndicatorIcon fontSize="small" sx={{ opacity: 0.5 }} /> Segure ≡ e arraste para reordenar
          </Typography>

          {/* Cabeçalho da tabela (div) */}
          <Box sx={{
            display: { xs: 'none', md: 'grid' },
            gridTemplateColumns: '52px 1fr 14% 9% 10% 7% 44px',
            px: 1, py: 0.5, mb: 0.5,
            bgcolor: 'transparent',
            borderRadius: 1,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            {['#', 'Título', 'Fonte', 'Tipo', 'Data', 'Tier', ''].map((h, i) => (
              <Typography key={i} variant="caption" fontWeight={700} color="text.secondary">{h}</Typography>
            ))}
          </Box>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="selected-articles">
              {(provided, snapshot) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{ bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'transparent', borderRadius: 1, minHeight: 40 }}
                >
                  {selectedArticles.map((article, index) => (
                    <Draggable key={article._id} draggableId={article._id} index={index}>
                      {(provided, snapshot) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '42px minmax(0, 1fr) 40px', md: '52px 1fr 14% 9% 10% 7% 44px' },
                            alignItems: 'center',
                            px: 1, py: 0.5,
                            mb: 0.4,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: snapshot.isDragging ? 'primary.main' : 'divider',
                            bgcolor: snapshot.isDragging ? 'action.selected' : 'transparent',
                            boxShadow: snapshot.isDragging ? 4 : 0,
                            '& > :nth-of-type(3), & > :nth-of-type(4), & > :nth-of-type(5), & > :nth-of-type(6)': {
                              display: { xs: 'none', md: 'initial' }
                            }
                          }}
                        >
                          {/* Handle + número */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box {...provided.dragHandleProps} sx={{ color: 'text.disabled', cursor: 'grab', display: 'flex', lineHeight: 0 }}>
                              <DragIndicatorIcon sx={{ fontSize: 18 }} />
                            </Box>
                            <Box sx={{
                              minWidth: 20, height: 20, borderRadius: '50%',
                              bgcolor: 'primary.main', color: 'primary.contrastText',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '0.6rem', fontWeight: 700
                            }}>{index + 1}</Box>
                          </Box>

                          {/* Título */}
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ pr: 1 }}>
                            {article.title}
                          </Typography>

                          {/* Fonte */}
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ pr: 1 }}>
                            {article.siteName}
                          </Typography>

                          {/* Tipo */}
                          <Box>
                            {article.mediaType && (
                              <Chip label={article.mediaType} size="small" sx={{ height: 16, fontSize: '0.6rem' }} />
                            )}
                          </Box>

                          {/* Data */}
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {new Date(article.date || article.createdAt).toLocaleDateString('pt-BR')}
                          </Typography>

                          {/* Tier */}
                          <Box>
                            {getEffectiveTier(article) && (
                              <Chip label={`Tier ${getEffectiveTier(article)}`} size="small"
                                color={getEffectiveTier(article) === 1 ? 'success' : getEffectiveTier(article) === 2 ? 'warning' : 'default'}
                                sx={{ height: 16, fontSize: '0.6rem' }} />
                            )}
                          </Box>

                          {/* Remover */}
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <IconButton size="small" color="error" onClick={() => handleArticleSelect(article)}>
                              <DeleteIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        </Paper>
      )}


      {/* DateRangePicker */}
      {dateRangeOpen && (
        <DateRangePicker label="Período Personalizado" value={customDateRange}
          onChange={(range) => {
            setCustomDateRange(range); setPeriod('custom'); setDateRangeOpen(false);
            loadArticles({ period: 'custom', dateRange: range });
          }}
          onClose={() => setDateRangeOpen(false)} />
      )}
    </Box>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  const renderContactSelection = () => (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Gerenciar Contatos</Typography>
          {newsletter.selectedContacts.length > 1 && (
            <Button 
              variant="outlined" 
              color="secondary" 
              startIcon={<PeopleIcon />}
              onClick={() => setQuickGroupDialogOpen(true)}
              sx={{ 
                borderRadius: 20, 
                textTransform: 'none',
                fontWeight: 600,
                borderWidth: '2px',
                '&:hover': { borderWidth: '2px' }
              }}
            >
              Criar Grupo ({newsletter.selectedContacts.length})
            </Button>
          )}
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setContactDialogOpen(true)}>
          Adicionar Contato
        </Button>
      </Box>

      {/* SEÇÃO DE GRUPOS */}
      {groups.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Seus Grupos ({groups.length})
          </Typography>
          <Grid container spacing={2}>
            {groups.map((group) => {
              const isSelected = newsletter.selectedGroups.includes(group._id);
              return (
                <Grid item xs={12} sm={6} md={3} lg={2} key={group._id}>
                  <Paper
                    elevation={0}
                    onClick={() => {
                      if (isSelected) {
                        setNewsletter(prev => ({ ...prev, selectedGroups: prev.selectedGroups.filter(id => id !== group._id) }));
                      } else {
                        setNewsletter(prev => ({ ...prev, selectedGroups: [...prev.selectedGroups, group._id] }));
                      }
                    }}
                    sx={{
                      p: 1.5,
                      pr: 4.5, // Espaço extra para o menu de ações
                      borderRadius: 3, // Padrão oficial lg (12px)
                      border: '1px solid',
                      borderColor: isSelected ? 'warning.main' : 'divider',
                      bgcolor: isSelected ? 'warning.main' : 'transparent',
                      color: isSelected ? '#000' : 'text.primary',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      position: 'relative',
                      '&:hover': { 
                        borderColor: 'warning.main', 
                        bgcolor: isSelected ? 'warning.main' : 'action.hover',
                        '& .action-menu': { opacity: 1 }
                      }
                    }}
                  >
                    <Box sx={{ 
                      position: 'absolute', 
                      top: '50%', 
                      right: 4, 
                      transform: 'translateY(-50%)',
                      opacity: { xs: 1, md: 0 }, 
                      transition: 'opacity 0.2s',
                      zIndex: 10
                    }} className="action-menu">
                      <IconButton 
                        size="small" 
                        onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, 'group', group); }}
                        sx={{ color: 'inherit', p: 0.5 }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Avatar sx={{ 
                      bgcolor: isSelected ? 'rgba(0,0,0,0.2)' : (group.color || 'primary.main'), 
                      color: isSelected ? '#000' : '#fff',
                      width: 28, 
                      height: 28 
                    }}>
                      <PeopleIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 700, color: 'inherit', lineHeight: 1.2 }}>{group.name}</Typography>
                      <Tooltip 
                        title={
                          <Box sx={{ p: 0.5 }}>
                            {group.contacts?.slice(0, 15).map(c => (
                              <Typography key={c._id} variant="caption" display="block" sx={{ whiteSpace: 'nowrap' }}>
                                • {c.name || 'Sem nome'} ({c.email})
                              </Typography>
                            ))}
                            {(group.contacts?.length > 15) && (
                              <Typography variant="caption" sx={{ mt: 0.5, fontWeight: 700, display: 'block', borderTop: '1px solid rgba(255,255,255,0.2)', pt: 0.5 }}>
                                + {group.contacts.length - 15} outros contatos
                              </Typography>
                            )}
                            {(!group.contacts || group.contacts.length === 0) && (
                              <Typography variant="caption">Nenhum contato neste grupo</Typography>
                            )}
                          </Box>
                        }
                        arrow
                        placement="top"
                      >
                        <Typography variant="caption" sx={{ color: isSelected ? 'rgba(0,0,0,0.6)' : 'text.secondary', display: 'block', lineHeight: 1, cursor: 'help' }}>
                          {group.contacts?.length || 0} contatos
                        </Typography>
                      </Tooltip>
                    </Box>
                    <Checkbox checked={isSelected} size="small" sx={{ p: 0, color: isSelected ? '#000' : 'action.disabled', '&.Mui-checked': { color: '#000' } }} />
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
          <Divider sx={{ mt: 4 }} />
        </Box>
      )}

      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Contatos Individuais
      </Typography>

      {contacts.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '1px dashed', borderColor: 'divider', bgcolor: 'transparent' }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Você ainda não tem contatos cadastrados.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setContactDialogOpen(true)} sx={{ mt: 2, borderRadius: 2 }}>
            Adicionar Primeiro Contato
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={1.5}>
          {contacts.map((contact) => {
            const isSelected = newsletter.selectedContacts.includes(contact._id);
            return (
              <Grid item xs={12} sm={6} md={3} lg={2} key={contact._id}>
                <Paper
                  elevation={0}
                  onClick={() => {
                    if (isSelected) {
                      setNewsletter(prev => ({ ...prev, selectedContacts: prev.selectedContacts.filter(id => id !== contact._id) }));
                    } else {
                      setNewsletter(prev => ({ ...prev, selectedContacts: [...prev.selectedContacts, contact._id] }));
                    }
                  }}
                  sx={{
                    p: 1.5,
                    pr: 4.5, // Espaço extra para o menu de ações
                    borderRadius: 3, // Padrão oficial lg (12px)
                    border: '1px solid',
                    borderColor: isSelected ? 'warning.main' : 'divider',
                    bgcolor: isSelected ? 'warning.main' : 'background.paper',
                    color: isSelected ? '#000' : 'text.primary',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    '&:hover': {
                      borderColor: 'warning.main',
                      transform: 'translateY(-1px)',
                      boxShadow: 1,
                      '& .action-menu': { opacity: 1 }
                    }
                  }}
                >
                  <Box sx={{ 
                    position: 'absolute', 
                    top: '50%', 
                    right: 4, 
                    transform: 'translateY(-50%)',
                    opacity: { xs: 1, md: 0 }, 
                    transition: 'opacity 0.2s',
                    zIndex: 10
                  }} className="action-menu">
                    <IconButton 
                      size="small" 
                      onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, 'contact', contact); }}
                      sx={{ color: 'inherit', p: 0.5 }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Avatar sx={{ 
                    width: 30, 
                    height: 30, 
                    bgcolor: isSelected ? 'rgba(0,0,0,0.2)' : 'action.selected', 
                    color: isSelected ? '#000' : 'text.primary', 
                    fontSize: '0.875rem', 
                    fontWeight: 600 
                  }}>
                    {contact.name.charAt(0).toUpperCase()}
                  </Avatar>
                  
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: 'inherit', lineHeight: 1.2 }}>
                      {contact.name}
                    </Typography>

                    <Typography variant="caption" noWrap sx={{ color: isSelected ? 'rgba(0,0,0,0.6)' : 'text.secondary', display: 'block', lineHeight: 1 }}>
                      {contact.email}
                    </Typography>
                  </Box>
                  
                  <Checkbox
                    checked={isSelected}
                    disableRipple
                    sx={{ p: 0.5, color: isSelected ? '#000' : 'action.disabled', '&.Mui-checked': { color: '#000' } }}
                  />
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );

  const renderNewsletterCreation = () => (
    <Box>
      <TextField fullWidth label="Assunto do Email" value={newsletter.subject}
        onChange={(e) => setNewsletter({ ...newsletter, subject: e.target.value })} sx={{ mb: 3 }} />
      <TextField fullWidth multiline rows={4} label="Mensagem Personalizada (opcional)"
        value={newsletter.message} onChange={(e) => setNewsletter({ ...newsletter, message: e.target.value })} sx={{ mb: 3 }} />
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Template de Design
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {templates.filter(t => t.isActive).map(t => {
            const isSelected = newsletter.templateId === t._id.toString();
            return (
              <Chip
                key={t._id}
                label={t.name}
                onClick={() => {
                  setNewsletter(prev => ({ ...prev, templateId: t._id.toString() }));
                  saveTemplatePreference(t._id.toString());
                }}
                icon={<EmailIcon />}
                variant={isSelected ? 'filled' : 'outlined'}
                color={isSelected ? 'warning' : 'default'}
                sx={{ 
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 1,
                  '& .MuiChip-icon': { color: isSelected ? '#000' : 'text.secondary' }
                }}
              />
            );
          })}
          {templates.filter(t => t.isActive).length === 0 && (
            <Typography variant="body2" color="text.secondary">Nenhum template cadastrado</Typography>
          )}
        </Box>
      </Box>
      <FormControlLabel
        control={<Checkbox checked={newsletter.groupByTags} onChange={(e) => setNewsletter({ ...newsletter, groupByTags: e.target.checked })} />}
        label="Agrupar artigos por Tags" sx={{ mb: 1 }}
      />
      <FormControlLabel
        control={<Checkbox checked={newsletter.showImages} onChange={(e) => setNewsletter({ ...newsletter, showImages: e.target.checked })} />}
        label="Mostrar miniaturas dos artigos" sx={{ mb: 3 }}
      />
      <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Preview da Newsletter</Typography>
          <Button variant="outlined" onClick={handlePreviewNewsletter} startIcon={isMobile ? <VisibilityIcon /> : undefined}
            disabled={previewLoading || selectedArticles.length === 0}>
            {previewLoading ? 'Gerando...' : (isMobile ? 'Visualizar' : 'Gerar Visualização')}
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary">Assunto: {newsletter.subject || 'Sem assunto'}</Typography>
        <Typography variant="body2" color="text.secondary">
          Artigos: {selectedArticles.length} no envio
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Destinatários: {newsletter.selectedContacts.length} contatos | {newsletter.selectedGroups.length} grupos
        </Typography>
        {previewHtml && !isMobile && (
          <Box sx={{ mt: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', height: 600 }}>
            <iframe srcDoc={previewHtml} title="Newsletter Preview" style={{ width: '100%', height: '100%', border: 'none' }} />
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Container maxWidth={false} sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, sm: 2 }, pb: { xs: 12, md: 4 }, width: '100%' }}>
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1, m: 0, fontWeight: 700, color: 'text.primary', fontSize: { xs: '1.35rem', md: '2.125rem' } }}>
            <EmailIcon color="primary" />
            Criação de Newsletter
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' }, mt: 0.5, ml: 4.5, mb: 3 }}>
            Crie sua campanha de email utilizando conteúdos capturados do monitoramento.
          </Typography>
        </Box>
      </Box>
      <NewsletterTabs />

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper elevation={0} sx={{ p: { xs: 1.25, md: 4 }, width: '100%', borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 2, md: 5 } }}>
          <ButtonGroup fullWidth={isMobile} size="medium" variant="outlined" sx={{ borderRadius: 2, '& .MuiButtonGroup-grouped': { borderColor: 'divider' } }}>
            <Button 
              variant={activeTab === 0 ? 'contained' : 'outlined'}
              onClick={() => { setActiveTab(0); setActiveStep(0); }}
              sx={{ 
                px: { xs: 1, sm: 4 },
                py: 1,
                fontWeight: activeTab === 0 ? 700 : 500,
                textTransform: 'none',
                minWidth: { xs: 0, sm: 140 }, fontSize: { xs: '0.72rem', sm: '0.875rem' }
              }}
            >
              {isMobile ? '1. Artigos' : '1. Artigos'}
            </Button>
            <Button 
              variant={activeTab === 1 ? 'contained' : 'outlined'}
              onClick={() => { setActiveTab(1); setActiveStep(1); }}
              sx={{ 
                px: { xs: 1, sm: 4 },
                py: 1,
                fontWeight: activeTab === 1 ? 700 : 500,
                textTransform: 'none',
                minWidth: { xs: 0, sm: 140 }, fontSize: { xs: '0.72rem', sm: '0.875rem' }
              }}
            >
              2. Contatos
            </Button>
            <Button 
              variant={activeTab === 2 ? 'contained' : 'outlined'}
              onClick={() => { setActiveTab(2); setActiveStep(2); }}
              sx={{ 
                px: { xs: 1, sm: 4 },
                py: 1,
                fontWeight: activeTab === 2 ? 700 : 500,
                textTransform: 'none',
                minWidth: { xs: 0, sm: 140 }, fontSize: { xs: '0.72rem', sm: '0.875rem' }
              }}
            >
              {isMobile ? '3. Config.' : '3. Configuração'}
            </Button>
          </ButtonGroup>
        </Box>

        {activeTab === 0 && renderArticleSelection()}
        {activeTab === 1 && renderContactSelection()}
        {activeTab === 2 && renderNewsletterCreation()}

        <Box sx={{ mt: 3, mb: 1, display: 'flex', justifyContent: 'center' }}>
          {activeStep === 0 && (
            <Alert 
              variant="filled"
              elevation={0}
              severity={selectedArticles.length > 0 ? 'success' : 'warning'} 
              sx={{ borderRadius: 2, fontWeight: 500 }}
              iconMapping={{
                success: <CheckCircleIcon fontSize="inherit" />
              }}
            >
              {selectedArticles.length > 0 
                ? `${selectedArticles.length} artigo(s) preparado(s) para a newsletter` 
                : 'Selecione pelo menos um artigo para prosseguir avançando'}
            </Alert>
          )}
          {activeStep === 1 && (
            <Alert 
              variant="filled"
              elevation={0}
              severity={(newsletter.selectedContacts.length > 0 || newsletter.selectedGroups.length > 0) ? 'success' : 'warning'} 
              sx={{ borderRadius: 2, fontWeight: 500 }}
              iconMapping={{
                success: <CheckCircleIcon fontSize="inherit" />
              }}
            >
              {(newsletter.selectedContacts.length > 0 || newsletter.selectedGroups.length > 0) 
                ? `${newsletter.selectedContacts.length} contato(s) e ${newsletter.selectedGroups.length} grupo(s) selecionados` 
                : 'Selecione pelo menos um destinatário para prosseguir'}
            </Alert>
          )}
        </Box>

        <Box sx={{
          mt: 4,
          display: 'flex',
          justifyContent: 'space-between',
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: { xs: 1.25, md: 3 },
          ...(isMobile && {
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1200,
            px: 2,
            pb: 'max(12px, env(safe-area-inset-bottom))',
            bgcolor: 'background.paper',
            boxShadow: '0 -4px 18px rgba(0,0,0,0.12)'
          })
        }}>
          <Button disabled={activeStep === 0} onClick={() => { setActiveStep(s => s - 1); setActiveTab(t => t - 1); }} sx={{ fontWeight: 600 }}>
            Voltar
          </Button>
          <Button variant="contained" disabled={loading} sx={{ fontWeight: 600, borderRadius: 2 }} onClick={async () => {
            if (activeStep === 0) {
              if (selectedArticles.length === 0) { setError('Selecione pelo menos um artigo'); return; }
              setActiveStep(1); setActiveTab(1);
            } else if (activeStep === 1) {
              if (newsletter.selectedContacts.length === 0 && newsletter.selectedGroups.length === 0) { 
                setError('Selecione pelo menos um destinatário'); 
                return; 
              }
              setActiveStep(2); setActiveTab(2);
            } else if (activeStep === 2) {
              await handleCreateNewsletter();
              setActiveStep(3); // Preparado para envio
            } else {
              await handleSendNewsletter();
            }
          }}>
            {loading ? <CircularProgress size={20} /> :
              activeStep === 2 ? 'Criar Newsletter' : activeStep === 3 ? 'Enviar' : 'Próximo'}
          </Button>
        </Box>
      </Paper>

      <Drawer
        anchor="bottom"
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        PaperProps={{ sx: { borderRadius: '18px 18px 0 0', maxHeight: '88vh' } }}
      >
        <Box sx={{ p: 2, overflowY: 'auto' }}>
          <BottomSheetHandle sx={{ mb: 2 }} />
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Filtrar artigos</Typography>
          <TextField
            fullWidth label="Buscar artigos" size="small" value={searchText}
            onChange={(e) => setSearchText(e.target.value)} sx={{ mb: 1.5 }}
          />
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select value={mediaTypeFilter} onChange={(e) => setMediaTypeFilter(e.target.value)} label="Tipo">
                  <MenuItem value="">Todos</MenuItem>
                  {['Online','YouTube','Instagram','Twitter','TikTok','Facebook','LinkedIn','Jornal','Revista','TV','Rádio'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Tier</InputLabel>
                <Select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} label="Tier">
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="Tier 1">Tier 1</MenuItem>
                  <MenuItem value="Tier 2">Tier 2</MenuItem>
                  <MenuItem value="Tier 3">Tier 3</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Tags</InputLabel>
                <Select
                  multiple
                  value={tagSelectValue}
                  onChange={handleTagsFilterChange}
                  label="Tags"
                  renderValue={() => tagsFilter === null
                    ? 'Todas as tags'
                    : tagsFilter.length === 0
                      ? 'Nenhuma tag'
                      : `${tagsFilter.length} selecionada(s)`}
                >
                  <MenuItem value={ALL_TAGS_VALUE}>
                    <Checkbox checked={tagsFilter === null} />
                    <ListItemText primary="Selecionar todas" />
                  </MenuItem>
                  <MenuItem value={CLEAR_TAGS_VALUE}>
                    <Checkbox checked={Array.isArray(tagsFilter) && tagsFilter.length === 0} />
                    <ListItemText primary="Limpar seleção" />
                  </MenuItem>
                  <Divider />
                  {availableTags.map(tag => (
                    <MenuItem key={tag._id} value={String(tag._id)}>
                      <Checkbox checked={tagsFilter === null || tagsFilter.includes(String(tag._id))} />
                      <ListItemText primary={tag.name} />
                    </MenuItem>
                  ))}
                  <MenuItem value={UNTAGGED_VALUE}>
                    <Checkbox checked={tagsFilter === null || tagsFilter.includes(UNTAGGED_VALUE)} />
                    <ListItemText primary="Sem tag" />
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              {renderSelectedTagChips()}
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Período</InputLabel>
                <Select value={period} onChange={(e) => {
                  if (e.target.value === 'custom') setDateRangeOpen(true);
                  else { setPeriod(e.target.value); setCustomDateRange(null); }
                }} label="Período">
                  {PERIOD_OPTIONS.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Checkbox checked={onlyUnsent} onChange={(e) => setOnlyUnsent(e.target.checked)} />}
            label="Mostrar apenas artigos ainda não enviados"
          />
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button fullWidth onClick={() => {
              setSearchText(''); setMediaTypeFilter(''); setTierFilter(''); setTagsFilter(null);
              setPeriod('all'); setCustomDateRange(null); setOnlyUnsent(false);
            }}>Limpar</Button>
            <Button fullWidth variant="contained" onClick={() => {
              setMobileFiltersOpen(false);
              loadArticles();
            }}>Aplicar filtros</Button>
          </Box>
        </Box>
      </Drawer>

      <Dialog
        fullScreen
        open={mobilePreviewOpen}
        onClose={() => setMobilePreviewOpen(false)}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
          <Typography fontWeight={700}>Preview da newsletter</Typography>
          <Button onClick={() => setMobilePreviewOpen(false)}>Fechar</Button>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: '#f4f4f4' }}>
          {previewHtml && <iframe srcDoc={previewHtml} title="Newsletter Preview Mobile" style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }} />}
        </DialogContent>
      </Dialog>

      <Dialog open={contactDialogOpen} onClose={() => { setContactDialogOpen(false); setEditingContact(null); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, backgroundImage: 'none' } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{editingContact ? 'Editar Contato' : 'Adicionar Contato'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Nome" value={newContact.name}
            onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} sx={{ mb: 2, mt: 1 }} />
          <TextField fullWidth label="Email" type="email" value={newContact.email}
            onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} sx={{ mb: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setContactDialogOpen(false); setEditingContact(null); }}>Cancelar</Button>
          <Button onClick={handleCreateContact} variant="contained">{editingContact ? 'Salvar Alterações' : 'Adicionar'}</Button>
        </DialogActions>
      </Dialog>

      {/* Novo: Dialog de Criação Rápida de Grupo */}
      {/* Diálogo de Criar/Editar Grupo (VERSÃO MINIMALISTA) */}
      <PlatformDialog 
        open={quickGroupDialogOpen} 
        onClose={() => { setQuickGroupDialogOpen(false); setEditingGroup(null); }} 
        maxWidth="xs" 
        fullWidth
      >
        <PlatformDialogTitle 
          title={editingGroup ? 'Editar Grupo' : 'Criar Novo Grupo'} 
          onClose={() => { setQuickGroupDialogOpen(false); setEditingGroup(null); }}
        />
        <DialogContent>
          {!editingGroup && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.825rem' }}>
              Criando grupo com {newsletter.selectedContacts.length} contatos.
            </Typography>
          )}
          <TextField 
            fullWidth 
            autoFocus
            label="Nome do Grupo" 
            size="small"
            placeholder="Ex: Diretoria"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)} 
            sx={{ mb: editingGroup ? 2 : 0, mt: 1 }} 
          />

          {editingGroup && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ mt: 2, mb: 1, fontWeight: 700, color: 'text.secondary', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Membros ({editGroupContactIds.length})
              </Typography>
              
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar contatos..."
                value={groupContactSearch}
                onChange={(e) => setGroupContactSearch(e.target.value)}
                sx={{ mb: 1 }}
              />

              <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', borderRadius: 2, bgcolor: 'transparent' }}>
                <List size="small" disablePadding>
                  {contacts
                    .filter(c => 
                      c.name?.toLowerCase().includes(groupContactSearch.toLowerCase()) || 
                      c.email?.toLowerCase().includes(groupContactSearch.toLowerCase())
                    )
                    .map((contact, index, arr) => {
                      const isMember = editGroupContactIds.includes(contact._id);
                      return (
                        <ListItem 
                          key={contact._id} 
                          dense 
                          button 
                          onClick={() => {
                            if (isMember) {
                              setEditGroupContactIds(prev => prev.filter(id => id !== contact._id));
                            } else {
                              setEditGroupContactIds(prev => [...prev, contact._id]);
                            }
                          }}
                          sx={{
                            py: 0.5,
                            borderBottom: index < arr.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider'
                          }}
                        >
                          <Checkbox 
                            size="small" 
                            checked={isMember} 
                            sx={{ p: 0.5, mr: 1 }}
                          />
                          <ListItemText 
                            primary={contact.name || 'Sem nome'} 
                            secondary={contact.email} 
                            primaryTypographyProps={{ 
                              variant: 'body2', 
                              fontWeight: isMember ? 600 : 400,
                              color: 'text.primary'
                            }}
                            secondaryTypographyProps={{ 
                              variant: 'caption'
                            }}
                          />
                        </ListItem>
                      );
                    })}
                  {contacts.length === 0 && (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Nenhum contato encontrado</Typography>
                    </Box>
                  )}
                </List>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setQuickGroupDialogOpen(false)} size="small" sx={{ color: 'text.secondary', textTransform: 'none' }}>Cancelar</Button>
          <Button 
            onClick={editingGroup ? handleUpdateGroupBasic : handleCreateGroupFromSelection} 
            variant="contained"
            color="primary"
            size="small"
            disabled={creatingGroup || !newGroupName.trim()}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            {creatingGroup ? <CircularProgress size={20} /> : (editingGroup ? 'Salvar' : 'Criar Grupo')}
          </Button>
        </DialogActions>
      </PlatformDialog>

      {/* Menu de Ações (Edit/Delete) */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 120 } }}
      >
        <MenuItem onClick={() => handleEditRequest(menuData?.type, menuData?.item)}>
          <EditIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
          Editar
        </MenuItem>
        <MenuItem onClick={() => handleDeleteRequest(menuData?.type, menuData?.item)} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} />
          Excluir
        </MenuItem>
      </Menu>

      {/* Diálogo de Confirmação de Exclusão */}
      <PlatformDialog 
        open={deleteConfirmOpen} 
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <PlatformDialogTitle
          title={`Excluir ${deleteTarget?.type === 'contact' ? 'Contato' : 'Grupo'}`}
          subtitle="Esta ação é irreversível e apagará todos os dados associados."
          icon={<WarningIcon />}
          onClose={() => setDeleteConfirmOpen(false)}
          color="error"
        />
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            Tem certeza que deseja excluir <strong>{deleteTarget?.data?.name}</strong> permanentemente?
          </Alert>
          
          {deleteTarget?.type === 'group' && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={deleteCascade} 
                    onChange={(e) => setDeleteCascade(e.target.checked)} 
                    color="error"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Excluir também todos os contatos deste grupo
                  </Typography>
                }
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 4 }}>
                <strong>Atenção:</strong> Isso removerá permanentemente os contatos do sistema em todos os outros grupos onde estiverem.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} sx={{ fontWeight: 600 }}>
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            sx={{ fontWeight: 700, borderRadius: 2, px: 3, boxShadow: 'none' }}
          >
            Excluir Permanentemente
          </Button>
        </DialogActions>
      </PlatformDialog>


      <NewsletterOrderDialog open={orderDialogOpen} onClose={() => setOrderDialogOpen(false)}
        articles={selectedArticles} globalTags={availableTags}
        initialTemplate={activeBrandData?.newsletterOrderTemplate} onApply={handleApplyOrder}
        orderHistory={orderHistory} orderHistoryLoading={orderHistoryLoading}
        sourceOrder={activeBrandData?.newsletterSourceOrder || []} />
      <NewsletterFinalOrderDialog open={finalOrderDialogOpen} onClose={() => setFinalOrderDialogOpen(false)}
        articles={selectedArticles} tagOrder={finalOrderTagsOrder} onConfirm={handleConfirmFinalOrder} />

      <ModernToast
        open={snackbarOpen}
        message={error || ''}
        severity="error"
        onClose={() => setSnackbarOpen(false)}
        duration={4000}
        position="bottom-center"
      />
    </Container>
  );
};

export default NewsletterPage;
